<template>
  <div :id="id" :style="style" />
</template>

<script>
import * as echarts from 'echarts/core'
import { BarChart, LineChart, PieChart } from 'echarts/charts'
import { TitleComponent, TooltipComponent, GridComponent, DatasetComponent, TransformComponent, GraphicComponent, LegendComponent, DataZoomComponent } from 'echarts/components'
import { CanvasRenderer } from 'echarts/renderers'
import theme from '@/components/chart/theme.json'
import helperMethods from '@/helpers/utility.js'
echarts.use([TitleComponent, TooltipComponent, GridComponent, DatasetComponent, TransformComponent, GraphicComponent, LegendComponent, DataZoomComponent, BarChart, LineChart, PieChart, CanvasRenderer])

export default {
  props: {
    id: {
      type: String
    },
    data: {
      type: Object
    },
    title: {
      type: String
    },
    scale: {
      type: String
    },
    width: {
      type: String,
      default: "100%"
    },
    height: {
      type: String,
      default: "560px"
    }
  },
  data() {
    return {
      chart: {},
      chartType: 'stacked_ver_bar',
      themeJson: theme
    }
  },
  mounted() {
    //this.chartType = this.selectedDatasetControls.chart_type
    this.formatData()
    this.init()
  },
  watch: {
    selectedDatasetControls: function () {
      //this.chartType = this.selectedDatasetControls.chart_type
      this.formatData()
      this.init()
    }
  },
  computed: {
    isAuthenticated () {
      return this.$store.getters['user/isAuthenticated']
    },
    isLicensed () {
      if (this.results && this.results.byId[this.selectedHash]) {
        return this.results.byId[this.selectedHash].attributes.licensed
      }

      return false
    },
    results () {
      return this.$store.getters['search/getResults'].dataset
    },
    selectedHash () {
      return this.$store.getters['search/getSelectedHash']
    },
    isDownloadingImage () {
      return this.$store.getters['app/isDownloadingImage']
    },
    selectedDatasetControls () {
      return this.$store.getters['search/getSelectedDatasetControls']
    },
    selectedChartTimeline () {
      return this.$store.getters['search/getSelectedChartTimeline']
    },
    style() {
      return {
        height: this.height,
        width: this.width,
        formattedData: {}
      }
    }
  },
  methods: {
    formatData() {
      this.chartType = this.selectedDatasetControls.chart_type
      let series = {}
      let formattedSeries = []
      let formattedKeys = []

      if (this.chartType === 'ver_bar' || this.chartType === 'hor_bar' || this.chartType === 'stacked_ver_bar' || this.chartType === 'stacked_hor_bar' || this.chartType === 'ver_line' || this.chartType === 'hor_line') {
        Object.entries(this.data).forEach(row => {
          let [key, value] = row

          series = {
            name: key,
            type: this.chartType === 'ver_bar' || this.chartType === 'hor_bar' || this.chartType === 'stacked_ver_bar' || this.chartType === 'stacked_hor_bar' ? 'bar' : 'line',
            data: Object.values(value),
            stack: this.chartType === 'ver_bar' || this.chartType === 'hor_bar' || this.chartType === 'ver_line' || this.chartType === 'hor_line' ? false : 'total',
            barMaxWidth: '75',
            itemStyle: {
              borderWidth: 0
            },
            symbol: 'circle',
            symbolSize: 9,
            lineStyle: {
              width: 3
            },
            emphasis: {
              focus: 'series'
            }
          }

          formattedSeries.push(series)
          formattedKeys = formattedKeys.concat(Object.keys(value))
        })

        this.formattedData = {
          series: formattedSeries,
          keys: [... new Set(formattedKeys)]
        }
      } else if (this.chartType === 'pie') {
        let keys = Object.keys(this.data)
        let that = this
        keys.forEach(function (key) {
          formattedSeries.push({
            name: key,
            value: that.data[key][that.selectedChartTimeline]
          })
        })

        this.formattedData = {
          series: formattedSeries
        }
      }
    },
    init() {
      let _this = this

      echarts.registerTheme('statzon', this.themeJson)
      let chart = echarts.init(document.getElementById(this.id), 'statzon')
      let option = {}

      if (this.chartType === 'ver_bar' || this.chartType === 'hor_bar' || this.chartType === 'stacked_ver_bar' || this.chartType === 'stacked_hor_bar' || this.chartType === 'ver_line' || this.chartType === 'hor_line') {
        option = {
          backgroundColor: 'rgb(27,30,33)',
          title: {
            show: true,
            text: '',
            left: "8px",
            top: "0",
            subtext: this.scale,
            textStyle: {
              color: 'rgb(255,255,255)',
              fontSize: 20,
              fontWeight: 'normal'
            },
            subtextStyle: {
              color: 'rgb(255,255,255)',
              fontSize: 16,
              fontWeight: 'normal'
            }
          },
          graphic: {
            elements: [
              {
                type: 'image',
                id: 'logo',
                left: 'center',
                top: '11%',
                z: -1,
                bounding: 'raw',
                style: {
                  image: '/img/statzon_logo_white.svg',
                  width: 400,
                  opacity: 0.01
                }
              }
            ]
          },
          legend: {
            show: false
          },
          grid: {
            left: '16',
            right: '40',
            top: '48',
            bottom: '40',
            containLabel: true
          },
          dataZoom: [{
            show: true,
            showDetail: false,
            type: 'slider',
            xAxisIndex: [0],
            filterMode: 'filter',
            height: '16',
            left: '8',
            right: '12',
            bottom: '16',
            backgroundColor: 'rgb(27,30,33)',
            dataBackground: {
              lineStyle: {
                color: 'rgba(255,255,255,0.1)',
                width: 2
              },
              areaStyle: {
                color: 'rgb(39,43,47)',
                opacity: 0.5
              }
            },
            selectedDataBackground: {
              lineStyle: {
                color: 'rgba(0,0,0,0.5)',
                width: 2
              },
              areaStyle: {
                color: 'rgba(255,255,255,0.1)',
                opacity: 1
              }
            },
            fillerColor: 'rgba(255,255,255,0.05)',
            handleIcon: 'image://url',
            handleSize: '16',
            handleStyle: {
              color: 'rgba(255,0,0,1)',
              borderColor: 'rgba(0,0,0,1)',
              borderWidth: 1,
              opacity: 0.25
            },
            moveHandleIcon: 'image://url',
            moveHandleSize: 6,
            moveHandleStyle: {
              color: 'rgba(255,255,255,0)',
              opacity: 1
            },
            emphasis: {
              handleIcon: 'path://M6 11v-4l-6 5 6 5v-4h12v4l6-5-6-5v4z',
              handleStyle: {
                borderColor: 'rgb(21,147,166)',
                opacity: 1
              },
              moveHandleStyle: {
                color: 'rgba(255,255,255,0.25)',
              },
            }
          }, {
            show: true,
            showDetail: false,
            type: 'slider',
            yAxisIndex: [0],
            filterMode: 'filter',
            width: '16',
            right: '10',
            top: '16',
            bottom: '46',
            backgroundColor: 'rgb(27,30,33)',
            dataBackground: {
              lineStyle: {
                color: 'rgba(255,255,255,0.1)',
                width: 2
              },
              areaStyle: {
                color: 'rgb(39,43,47)',
                opacity: 0.5
              }
            },
            selectedDataBackground: {
              lineStyle: {
                color: 'rgba(0,0,0,0.5)',
                width: 2
              },
              areaStyle: {
                color: 'rgba(255,255,255,0.1)',
                opacity: 1
              }
            },
            fillerColor: 'rgba(255,255,255,0.05)',
            handleIcon: 'image://url',
            handleSize: '16',
            handleStyle: {
              color: 'rgba(255,0,0,1)',
              borderColor: 'rgba(0,0,0,1)',
              borderWidth: 1,
              opacity: 0.25
            },
            moveHandleIcon: 'image://url',
            moveHandleSize: 6,
            moveHandleStyle: {
              color: 'rgba(255,255,255,0)',
              opacity: 1
            },
            emphasis: {
              handleStyle: {
                borderColor: 'rgb(21,147,166)',
                opacity: 1
              },
              moveHandleStyle: {
                color: 'rgba(255,255,255,0.25)',
              },
            }
          }],
          yAxis: this.chartType === 'ver_bar' || this.chartType === 'stacked_ver_bar' || this.chartType === 'hor_line' ? {
            show: true,
            type: 'value',
            axisLabel: {
              fontSize: 14,
              formatter: function (value, index) {
                return _this.isLicensed ? _this.formatNumber(value, 1) : '??'
              }
            }
          } : {
            show: true,
            type: 'category',
            data: this.formattedData.keys,
            axisTick: {
              show: false
            },
            axisLabel: {
              fontSize: 14
            }
          },
          xAxis: this.chartType === 'hor_bar' || this.chartType === 'stacked_hor_bar' || this.chartType === 'ver_line' ? {
            show: true,
            type: 'value',
            axisLabel: {
              fontSize: 14,
              formatter: function (value, index) {
                return _this.isLicensed ? _this.formatNumber(value, 1) : '??'
              }
            }
          } : {
            show: true,
            type: 'category',
            data: this.formattedData.keys,
            axisTick: {
              show: false
            },
            axisLabel: {
              fontSize: 14
            }
          },
          series: this.formattedData.series
        }
      }

      else if (this.chartType === 'pie') {
        option = {
          title: {
            show: true,
            text: '',
            left: "8px",
            top: "0",
            subtext: this.scale,
            textStyle: {
              color: 'rgb(255,255,255)',
              fontSize: 20,
              fontWeight: 'normal'
            },
            subtextStyle: {
              color: 'rgb(255,255,255)',
              fontSize: 16,
              fontWeight: 'normal'
            }
          },
          backgroundColor: 'rgb(27,30,33)',
          graphic: {
            elements: [
              {
                type: 'image',
                id: 'logo',
                left: 'center',
                top: '11%',
                z: -1,
                bounding: 'raw',
                style: {
                  image: '/img/statzon_logo_white.svg',
                  width: 400,
                  opacity: 0.01
                }
              }
            ]
          },
          legend: {
            show: false
          },
          grid: {
            left: '16',
            right: '40',
            top: '48',
            bottom: '40',
            containLabel: true
          },
          dataZoom: [{
            show: false
          },{
            show: false
          }],
          yAxis: {
            show: false,
          },
          xAxis: {
            show: false,
          },
          avoidLabelOverlap: true,
          stillShowZeroSum: false,
          
          series: [{
            name: this.selectedChartTimeline,
            type: 'pie',
            radius: '60%',
            center: ['50%', '50%'],
            itemStyle: {
              borderWidth: 2,
              borderColor: 'rgb(27, 30, 33)',
            },
            labelLayout: {
              hideOverlap: true
            },
            emphasis: {
              itemStyle: {
                
              }
            },
            data: this.formattedData.series
          }]
        }
      }

      chart.setOption(option)
    },
    ...helperMethods
  }
}
</script>
